import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';

/**
 * Scrollable product image gallery for products with multiple variants
 * Shows main image with thumbnail navigation and auto-scroll
 */
const ProductGallery = ({ images, productName, className = '', style = {} }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const panelBg = isDark ? '#262626' : '#f9f9f9';
  const emptyBg = isDark ? '#262626' : '#f5f5f5';
  const mutedText = isDark ? '#c6c6c6' : '#666666';
  const thumbBorder = isDark ? '#525252' : '#e0e0e0';
  const thumbBorderHover = isDark ? '#8d8d8d' : '#999999';
  const scrollbarThumb = isDark ? '#525252' : '#cccccc';
  const scrollbarThumbHover = isDark ? '#6f6f6f' : '#999999';

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const scrollContainerRef = useRef(null);
  const autoScrollInterval = useRef(null);

  // Early return if no images
  if (!images || images.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        backgroundColor: emptyBg,
        borderRadius: '8px'
      }}>
        <span style={{ color: mutedText }}>No images available</span>
      </div>
    );
  }

  // Filter out invalid images
  const validImages = images.filter(img => img && img.url);

  // Early return if no valid images
  if (validImages.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        backgroundColor: emptyBg,
        borderRadius: '8px'
      }}>
        <span style={{ color: mutedText }}>No valid images available</span>
      </div>
    );
  }

  // Auto-scroll through images
  useEffect(() => {
    if (isAutoScrolling && validImages.length > 1) {
      autoScrollInterval.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % validImages.length);
      }, 3000); // Change image every 3 seconds
    }

    return () => {
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
      }
    };
  }, [isAutoScrolling, validImages.length]);

  const goToImage = (index) => {
    setIsAutoScrolling(false);
    setCurrentIndex(index);
  };

  const scrollToThumbnail = (index) => {
    if (scrollContainerRef.current) {
      const thumbnailElements = scrollContainerRef.current.children;
      if (thumbnailElements[index]) {
        thumbnailElements[index].scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest'
        });
      }
    }
  };

  useEffect(() => {
    scrollToThumbnail(currentIndex);
  }, [currentIndex]);

  const currentImage = validImages[currentIndex % validImages.length];

  return (
    <div className={`product-gallery ${className}`} style={style}>
      {/* Main Image Display */}
      <div
        style={{
          position: 'relative',
          marginBottom: '16px',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: panelBg,
        }}
        onMouseEnter={() => setIsAutoScrolling(false)}
        onMouseLeave={() => setIsAutoScrolling(true)}
        onFocus={() => setIsAutoScrolling(false)}
        onBlur={() => setIsAutoScrolling(true)}
      >
        <img
          src={currentImage.url}
          alt={`${productName} - Image ${currentIndex + 1}`}
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: '400px',
            objectFit: 'contain',
            display: 'block',
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />

        {validImages.length > 1 && (
          <div
            style={{
              position: 'absolute',
              bottom: '8px',
              right: '8px',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: '#ffffff',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              pointerEvents: 'none',
            }}
            aria-live="polite"
          >
            {currentIndex + 1} / {validImages.length}
          </div>
        )}
      </div>

      {/* Thumbnail Navigation */}
      {validImages.length > 1 && (
        <div style={{
          position: 'relative'
        }}>
          <div
            ref={scrollContainerRef}
            style={{
              display: 'flex',
              gap: '8px',
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              scrollbarWidth: 'thin',
              scrollbarColor: `${scrollbarThumb} transparent`,
              padding: '4px 0'
            }}
            className="thumbnail-scroll"
          >
            {validImages.map((image, index) => (
              <button
                key={index}
                onClick={() => goToImage(index)}
                style={{
                  flexShrink: 0,
                  width: '60px',
                  height: '60px',
                  border: currentIndex === index ? '2px solid #0f62fe' : `2px solid ${thumbBorder}`,
                  borderRadius: '4px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  scrollSnapAlign: 'center',
                  transition: 'all 0.2s ease',
                  backgroundColor: panelBg
                }}
                className="thumbnail-button"
                onMouseEnter={(e) => {
                  if (currentIndex !== index) {
                    e.currentTarget.style.borderColor = thumbBorderHover;
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentIndex !== index) {
                    e.currentTarget.style.borderColor = thumbBorder;
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              >
                <img
                  src={image.url}
                  alt={`${productName} - Thumbnail ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .product-gallery .thumbnail-scroll::-webkit-scrollbar {
          height: 4px;
        }
        
        .product-gallery .thumbnail-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .product-gallery .thumbnail-scroll::-webkit-scrollbar-thumb {
          background-color: ${scrollbarThumb};
          border-radius: 2px;
        }
        
        .product-gallery .thumbnail-scroll::-webkit-scrollbar-thumb:hover {
          background-color: ${scrollbarThumbHover};
        }
        
      `}</style>
    </div>
  );
};

export default ProductGallery;
